<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import AppState from '@/states/app'
import { InfoCircleIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIForm,
  UIFormItem,
  UIInputNumber,
  UISelect,
  UISwitch,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarType } from '../../models/calendar'
import { getTimeSpanOptions, getTooltip } from '../../utils/generalFunctions'
import HeaderTile from '../common/HeaderTile.vue'
import DurationPicker from './DurationPicker.vue'

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  formData: {
    slotDuration: '30',
    slotDurationUnit: 'mins',
    slotInterval: '30',
    slotIntervalUnit: 'mins',
    allowBookingAfter: '',
    allowBookingAfterUnit: 'days',
    allowBookingFor: '',
    allowBookingForUnit: 'days',
    appoinmentPerSlot: 1 as number | undefined,
    appoinmentPerDay: 1 as number | undefined,
    preBuffer: '',
    preBufferUnit: 'mins',
    slotBuffer: '',
    slotBufferUnit: 'mins',
    lookBusyConfig: {
      enabled: false,
      lookBusyPercentage: 0,
    },
  },
})
const formRef = ref()

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const isCollectiveCalendar = computed(() => {
  return editingCalendar.value?.calendarType === CalendarType.COLLECTIVE
})

const rules = {
  appoinmentPerSlot: {
    validator() {
      if (editingCalendar.value?.calendarType === CalendarType.CLASS_BOOKING) {
        if (!state.formData.appoinmentPerSlot) {
          return new Error(t('settings.availability.schedule.this_is_required'))
        }

        if (state.formData.appoinmentPerSlot > 9999) {
          return new Error('This cannot be greater than 9999')
        }
      }

      if (
        state.formData.appoinmentPerSlot &&
        state.formData.appoinmentPerSlot < 0
      ) {
        return new Error('This cannot be less than 0')
      }

      return true
    },
    trigger: ['input', 'blur'],
  },
  appoinmentPerDay: {
    validator() {
      if (
        state.formData.appoinmentPerDay &&
        state.formData.appoinmentPerDay < 0
      ) {
        return new Error('This cannot be less than 0')
      }
      return true
    },
    trigger: ['input', 'blur'],
  },
}

const emit = defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    state.formData.slotDuration =
      editingCalendar.value?.slotDurationUnit === 'hours'
        ? String(editingCalendar.value?.slotDuration / 60 || '')
        : String(editingCalendar.value?.slotDuration || '')
    state.formData.slotDurationUnit =
      editingCalendar.value?.slotDurationUnit || 'mins'
    state.formData.slotInterval =
      editingCalendar.value?.slotIntervalUnit === 'hours'
        ? String(editingCalendar.value?.slotInterval / 60 || '')
        : String(editingCalendar.value?.slotInterval || '')
    state.formData.slotIntervalUnit =
      editingCalendar.value?.slotIntervalUnit || 'mins'

    state.formData.allowBookingFor = String(
      editingCalendar.value?.allowBookingFor || ''
    )
    state.formData.allowBookingForUnit =
      editingCalendar.value?.allowBookingForUnit || 'days'
    state.formData.allowBookingAfter = String(
      editingCalendar.value?.allowBookingAfter || ''
    )
    const allowBookingAfterUnit = editingCalendar.value?.allowBookingAfterUnit || 'days'
    state.formData.allowBookingAfterUnit = allowBookingAfterUnit === 'mins' ? 'minutes' : allowBookingAfterUnit
    state.formData.appoinmentPerSlot = editingCalendar.value?.appoinmentPerSlot
    state.formData.appoinmentPerDay = editingCalendar.value?.appoinmentPerDay

    state.formData.preBuffer =
      editingCalendar.value?.preBufferUnit === 'hours'
        ? String(editingCalendar.value?.preBuffer / 60 || '')
        : String(editingCalendar.value?.preBuffer || '')
    state.formData.preBufferUnit =
      editingCalendar.value?.preBufferUnit || 'mins'
    state.formData.slotBuffer =
      editingCalendar.value?.slotBufferUnit === 'hours'
        ? String(editingCalendar.value?.slotBuffer / 60 || '')
        : String(editingCalendar.value?.slotBuffer || '')
    state.formData.slotBufferUnit =
      editingCalendar.value?.slotBufferUnit || 'mins'

    state.formData.lookBusyConfig = editingCalendar.value?.lookBusyConfig || {
      enabled: false,
      lookBusyPercentage: 0,
    }
  },
  async emitSave() {
    try {
      await formRef.value.getForm().validate()
    } catch (error) {
      return
    }
    if (
      AppState.errorInDurationPickerMap.slotDuration ||
      AppState.errorInDurationPickerMap.slotInterval ||
      AppState.errorInDurationPickerMap.slotBuffer ||
      AppState.errorInDurationPickerMap.preBuffer ||
      AppState.errorInDurationPickerMap.allowBookingAfter
    ) {
      return null
    }
    const payload = {
      slotDuration:
        state.formData.slotDurationUnit === 'mins'
          ? parseInt(state.formData.slotDuration)
          : parseInt(state.formData.slotDuration) * 60,
      slotDurationUnit: state.formData.slotDurationUnit,
      slotInterval:
        state.formData.slotIntervalUnit === 'mins'
          ? parseInt(state.formData.slotInterval)
          : parseInt(state.formData.slotInterval) * 60,
      slotIntervalUnit: state.formData.slotIntervalUnit,
      slotBuffer:
        state.formData.slotBufferUnit === 'mins'
          ? parseInt(state.formData.slotBuffer)
          : parseInt(state.formData.slotBuffer) * 60,
      slotBufferUnit: state.formData.slotBufferUnit,
      preBuffer:
        state.formData.preBufferUnit === 'mins'
          ? parseInt(state.formData.preBuffer)
          : parseInt(state.formData.preBuffer) * 60,
      preBufferUnit: state.formData.preBufferUnit,
      allowBookingFor: parseInt(state.formData.allowBookingFor),
      allowBookingForUnit: state.formData.allowBookingForUnit,
      allowBookingAfter: state.formData.allowBookingAfter
        ? parseInt(state.formData.allowBookingAfter)
        : 0,
      allowBookingAfterUnit: state.formData.allowBookingAfterUnit === 'minutes' ? 'mins' : state.formData.allowBookingAfterUnit,
      appoinmentPerSlot: state.formData.appoinmentPerSlot
        ? Number(state.formData.appoinmentPerSlot)
        : '',
      appoinmentPerDay: state.formData.appoinmentPerDay
        ? Number(state.formData.appoinmentPerDay)
        : '',
      lookBusyConfig: state.formData.lookBusyConfig,
    }

    return payload
  },
}

const bufferOptionForServices = computed(() => {
  const MAX_BUFFER = 90
  return getTimeSpanOptions(MAX_BUFFER, 0, 15)
})

defineExpose({
  methods,
})

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="booking-rules-header"
      :header="$t('calendar_advanced.booking_rules.booking_rules')"
      :description="
        $t('calendar_advanced.booking_rules.booking_rules_description')
      "
    />
    <div class="my-[15px] border-t border-gray-200" />

    <UIForm
      id="booking-rules-form"
      class="w-full"
      :model="state.formData"
      :rules="rules"
      ref="formRef"
    >
      <div class="flex w-full space-x-8">
        <DurationPicker
          class="w-1/2"
          :label="
            isServiceCalendar
              ? $t('calendar_advanced.availability.service_interval')
              : $t('calendar_advanced.availability.meeting_interval')
          "
          :tooltip="
            isServiceCalendar
              ? $t('calendar_advanced.availability.service_interval_tooltip')
              : $t('calendar_advanced.availability.meeting_interval_tooltip')
          "
          path="slotInterval"
          :value="state.formData.slotInterval"
          :unit="state.formData.slotIntervalUnit"
          @on-value-change="
            value => {
              state.formData.slotInterval = value.duration
              state.formData.slotIntervalUnit = value.timeUnit
            }
          "
        />
        <DurationPicker
          class="w-1/2"
          :label="
            isServiceCalendar
              ? $t('calendar_advanced.availability.service_duration')
              : $t('calendar_advanced.availability.meeting_duration')
          "
          :tooltip="
            isServiceCalendar
              ? $t('calendar_advanced.availability.service_duration_tooltip')
              : $t('calendar_advanced.availability.meeting_duration_tooltip')
          "
          path="slotDuration"
          :value="state.formData.slotDuration"
          :unit="state.formData.slotDurationUnit"
          @on-value-change="
            value => {
              state.formData.slotDuration = value.duration
              state.formData.slotDurationUnit = value.timeUnit
            }
          "
        />
      </div>

      <div class="flex w-full justify-between space-x-8">
        <DurationPicker
          class="w-1/2"
          :label="$t('calendar_advanced.availability.minimum_scheduling')"
          :tooltip="
            isServiceCalendar
              ? $t('calendar_advanced.availability.minimum_scheduling_tooltip')
              : $t(
                  'calendar_advanced.availability.minimum_scheduling_notice_tooltip'
                )
          "
          path="allowBookingAfter"
          :value="state.formData.allowBookingAfter"
          :unit="state.formData.allowBookingAfterUnit === 'minutes' ? 'mins' : state.formData.allowBookingAfterUnit"
          @on-value-change="
            value => {
              state.formData.allowBookingAfter = value.duration
              state.formData.allowBookingAfterUnit = value.timeUnit === 'mins' ? 'minutes' : value.timeUnit
            }
          "
        />
        <DurationPicker
          class="w-1/2"
          :label="$t('calendar_advanced.availability.date_range')"
          :tooltip="
            $t('calendar_advanced.availability.date_range_preferences_tooltip')
          "
          path="allowBookingFor"
          :value="state.formData.allowBookingFor"
          :unit="state.formData.allowBookingForUnit"
          @on-value-change="
            value => {
              state.formData.allowBookingFor = value.duration
              state.formData.allowBookingForUnit = value.timeUnit
            }
          "
        />
      </div>

      <UIForm v-if="isServiceCalendar" class="flex w-[49%]">
        <UIFormItem
          class="mt-2 w-full"
          :label="$t('calendar_advanced.availability.post_buffer_time')"
          path="slotBuffer"
        >
          <UISelect
            id="calendar-slot-buffer"
            :value="`${state.formData.slotBuffer || 0} mins`"
            class="w-full"
            :options="bufferOptionForServices"
            @update:value="state.formData.slotBuffer = $event"
          />
        </UIFormItem>
      </UIForm>

      <div
        v-if="
          ![CalendarType.SERVICE].includes(
            editingCalendar?.calendarType as CalendarType
          )
        "
        class="flex items-end space-x-8"
      >
        <div class="w-1/2">
          <DurationPicker
            :label="$t('calendar_advanced.availability.pre_buffer_time')"
            :tooltip="
              $t('calendar_advanced.availability.pre_buffer_time_tooltip')
            "
            path="preBuffer"
            :value="state.formData.preBuffer"
            :unit="state.formData.preBufferUnit"
            @on-value-change="
              value => {
                state.formData.preBuffer = value.duration
                state.formData.preBufferUnit = value.timeUnit
              }
            "
          />
        </div>
        <div class="w-1/2">
          <DurationPicker
            :tooltip="
              $t('calendar_advanced.availability.post_buffer_time_tooltip')
            "
            :label="$t('calendar_advanced.availability.post_buffer_time')"
            path="slotBuffer"
            :value="state.formData.slotBuffer"
            :unit="state.formData.slotBufferUnit"
            @on-value-change="
              value => {
                state.formData.slotBuffer = value.duration
                state.formData.slotBufferUnit = value.timeUnit
              }
            "
          />
        </div>
      </div>

      <div
        v-if="editingCalendar?.calendarType === CalendarType.CLASS_BOOKING"
        class="flex items-end space-x-8"
      >
        <div class="w-[49%]">
          <UIFormItem
            :label="$t('calendar_advanced.availability.seats_per_class')"
            path="appoinmentPerSlot"
            :tooltip="
              getTooltip(
                $t('calendar_advanced.availability.seats_per_class_tooltip')
              )
            "
          >
            <UIInputNumber
              id="calendar-appointments-per-seat"
              v-model="state.formData.appoinmentPerSlot"
              class="w-full"
              placeholder=""
            />
          </UIFormItem>
        </div>
      </div>

      <div
        v-if="
          ![CalendarType.CLASS_BOOKING, CalendarType.SERVICE].includes(
            editingCalendar?.calendarType as CalendarType
          )
        "
        class="mt-4 flex items-end space-x-8"
      >
        <div class="w-[48%]">
          <UIFormItem
            :label="
              $t('calendar_advanced.availability.maximum_bookings_per_day')
            "
            path="appoinmentPerDay"
            :tooltip="
              getTooltip(
                $t(
                  'calendar_advanced.availability.maximum_bookings_per_day_tooltip'
                )
              )
            "
          >
            <UIInputNumber
              id="calendar-appointments-perDay"
              v-model="state.formData.appoinmentPerDay"
              class="w-full"
              placeholder=""
            />
          </UIFormItem>
        </div>
        <div v-if="!isServiceCalendar && !isCollectiveCalendar" class="w-1/2">
          <UIFormItem
            :label="
              editingCalendar?.calendarType === CalendarType.EVENT ||
              editingCalendar?.calendarType === CalendarType.PERSONAL
                ? $t(
                    'calendar_advanced.availability.maximum_bookings_per_slot'
                  )
                : $t('calendar_advanced.availability.maximum_bookings_per_slot_per_user')
            "
            path="appoinmentPerSlot"
            :tooltip="
              getTooltip(
                // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
                $t(
                  editingCalendar?.calendarType === CalendarType.PERSONAL || editingCalendar?.calendarType === CalendarType.EVENT
                    ? 'calendar_advanced.availability.maximum_bookings_per_slot_tooltip_personal'
                    : 'calendar_advanced.availability.maximum_bookings_per_slot_tooltip'
                )
              )
            "
          >
            <UIInputNumber
              id="calendar-appointments-perSlot"
              v-model="state.formData.appoinmentPerSlot"
              class="w-full"
              placeholder=""
            />
          </UIFormItem>
        </div>
      </div>

      <div
        v-if="editingCalendar?.calendarType !== CalendarType.CLASS_BOOKING"
        class="mt-4 flex w-full flex-col gap-3 pb-3"
      >
        <div class="flex h-full w-full items-center space-x-3 p-0">
          <div id="calendar-lookBusy-switch">
            <UISwitch
              v-model:value="state.formData.lookBusyConfig.enabled"
              :aria-label="t('calendar_advanced.availability.look_busy_title')"
              :title="t('calendar_advanced.availability.look_busy_title')"
              size="small"
            />
          </div>
          <div class="flex items-center space-x-3">
            <div class="font-normal">
              {{ $t('calendar_advanced.availability.look_busy_title') }}
            </div>
            <UITooltip :placement="'top'">
              <template #trigger>
                <InfoCircleIcon class="w-4 text-gray-500" />
              </template>
              <p class="max-w-sm">
                {{ t('calendar_advanced.availability.look_busy_helper_text') }}
              </p>
            </UITooltip>
          </div>
        </div>
        <div class="w-full">
          <UIInputNumber
            class="w-[49%]"
            :id="'calendar-lookBusy-value'"
            v-model="state.formData.lookBusyConfig.lookBusyPercentage"
            :placeholder="''"
            :readonly="false"
            :loading="false"
            :clearable="false"
            :disabled="!state.formData.lookBusyConfig.enabled"
            :precision="undefined"
            :size="'medium'"
            :max="'100'"
            :min="'0'"
            :show-button="false"
          >
            <template #suffix>%</template>
          </UIInputNumber>
          <div
            class="leading-20 pt-2 text-left text-sm font-normal text-gray-500"
          >
            {{
              t(
                'calendar_advanced.availability.look_busy_percentage_description'
              )
            }}
          </div>
        </div>
      </div>
    </UIForm>
  </div>
</template>
